import React, { useEffect, useState } from 'react';
import { Header } from '../../../header/Header';
import MainSider from '../sider/MainSider/MainSider';
import MainContent from '../MainContent/MainContent';
import { MainHeader } from '../MainHeader/MainHeader';
import * as S from './MainLayout.styles';
import * as H from '@app/components/header/Header.styles';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { MEDICAL_DASHBOARD_PATH, NFT_DASHBOARD_PATH } from '@app/components/router/AppRouter';
import { useResponsive } from '@app/hooks/useResponsive';
import { References } from '@app/components/common/References/References';
import {
  checkRoot,
  isKaizenOnlyUser,
  setDayColorError,
  setDayColorGood,
  setDayColorWarn,
  setLogoTitle,
  setLogoUrl,
  setSettingId,
} from '@app/services/localStorage.service';
import { useLanguage } from '@app/hooks/useLanguage';
import { useTranslation } from 'react-i18next';
import SQDPAll from '@app/components/sqdp/all_factory/SQDP_all';
import { Col, Row } from 'antd';
import * as Auth from '@app/components/layouts/AuthLayout/AuthLayout.styles';
import { getSetting } from '@app/api/settings.api';
import { IN_DEVELOPMENT_MESSAGE, smartAuditOpenToAll } from '@app/api/backendProfile';
import { notificationController } from '@app/controllers/notificationController';
import { useSmartAuditVisible } from '@app/smart-audit/auth/useSmartAuditVisible';
import { ProfileDropdown } from '@app/components/header/components/profileDropdown/ProfileDropdown/ProfileDropdown';
import { SettingsDropdown } from '@app/components/header/components/settingsDropdown/SettingsDropdown';
import { HeaderFullscreen } from '@app/components/header/components/HeaderFullscreen/HeaderFullscreen';
import footer1 from '@app/assets/images/footer1.png';
import footer2 from '@app/assets/images/footer2.png';
import footer3 from '@app/assets/images/footer3.png';
import footer4 from '@app/assets/images/footer4.png';
import footer5 from '@app/assets/images/footer5.png';
import footer6 from '@app/assets/images/footer6.png';
import footer7 from '@app/assets/images/footer7.png';
// @ts-ignore
import HitachiStencilLogo from '@app/assets/logo_black.jpg';

const MainLayout: React.FC = () => {
  const [isTwoColumnsLayout, setIsTwoColumnsLayout] = useState(true);
  const [siderCollapsed, setSiderCollapsed] = useState(true);

  const { isDesktop } = useResponsive();
  const location = useLocation();
  const { t } = useTranslation();
  const { language } = useLanguage();

  const toggleSider = () => setSiderCollapsed(!siderCollapsed);
  const navigate = useNavigate();
  const [system, setSystem] = useState<string>();

  const homeKaizenOnly = isKaizenOnlyUser();

  // Spec §5.5: while Smart Audit is in development every session sees the tile, without `/me`; once it is open to
  // all, a session gets the tile by `/me` (D9).
  const saOpenToAll = smartAuditOpenToAll();
  const saVisibleByMe = useSmartAuditVisible(location.pathname === '/' && !homeKaizenOnly && saOpenToAll);
  const saVisible = !saOpenToAll || saVisibleByMe;

  /** A session gets "Đang phát triển" while Smart Audit is not open to all (spec R4). */
  const openSmartAudit = () => {
    if (!saOpenToAll) notificationController.info({ message: IN_DEVELOPMENT_MESSAGE });
    else navigate('/smart-audit');
  };

  useEffect(() => {
    setIsTwoColumnsLayout([MEDICAL_DASHBOARD_PATH, NFT_DASHBOARD_PATH].includes(location.pathname) && isDesktop);
    if (
      !!location.pathname.split('/')[1] &&
      !['spa', 'fac', 'calib', 'kaizen'].includes(location.pathname.split('/')[1])
    ) {
      getSetting().then((respSetting) => {
        if (respSetting?.data) {
          const data = respSetting.data;
          setSettingId(data.id, location.pathname.split('/')[1]);
          setLogoTitle(data.logoTitle, location.pathname.split('/')[1]);
          setLogoUrl(data.logoImageUrl, location.pathname.split('/')[1]);
          setDayColorGood(data.dayColorGood, location.pathname.split('/')[1]);
          setDayColorWarn(data.dayColorWarn, location.pathname.split('/')[1]);
          setDayColorError(data.dayColorError, location.pathname.split('/')[1]);
        }
      });
    }
  }, [location.pathname, isDesktop]);

  return (
    <>
      {location.pathname === '/' ? (
        <>
          {system !== 'gemba' || homeKaizenOnly ? (
            <Auth.Wrapper>
              <Auth.BackgroundHeader>
                <Row style={{ width: '100%' }}>
                  <Auth.HeaderTitleCol
                    span={14}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Auth.TextHeaderLeft>VNFF- FACTORY DASHBOARD</Auth.TextHeaderLeft>
                  </Auth.HeaderTitleCol>
                  <Auth.HeaderControlsCol
                    span={10}
                    style={{
                      justifyContent: 'end',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <H.ProfileColumn $isTwoColumnsLayout={false} style={{ paddingRight: '1rem' }}>
                      <Row align="middle" justify="end" gutter={[10, 10]}>
                        <Col>
                          <Row gutter={[{ xxl: 10 }, { xxl: 10 }]}>
                            <Auth.HeaderFullscreenCol>
                              <HeaderFullscreen />
                            </Auth.HeaderFullscreenCol>
                            <Col>
                              <SettingsDropdown />
                            </Col>
                          </Row>
                        </Col>

                        <Col>
                          <ProfileDropdown />
                        </Col>
                        <Col style={{ marginLeft: 10 }}>
                          <Auth.LogoHitachi src={HitachiStencilLogo} alt="Logo" />
                        </Col>
                      </Row>
                    </H.ProfileColumn>
                  </Auth.HeaderControlsCol>
                </Row>
              </Auth.BackgroundHeader>
              <Auth.BackgroundWrapper>
                <Auth.LoginWrapper>
                  <Col style={{ width: '50vw' }}>
                    {!homeKaizenOnly && (
                      <>
                        <Row>
                          <S.ButtonLayoutMain onClick={() => setSystem('gemba')}>Gemba</S.ButtonLayoutMain>
                        </Row>
                        <Row style={{ marginTop: '25px' }}>
                          <S.ButtonLayoutMain onClick={() => navigate('/calib/dashboard')}>Calib</S.ButtonLayoutMain>
                        </Row>
                      </>
                    )}
                    <Row style={homeKaizenOnly ? undefined : { marginTop: '25px' }}>
                      <S.ButtonLayoutMain onClick={() => navigate('/kaizen/actions/query')}>Kaizen</S.ButtonLayoutMain>
                    </Row>
                    {!homeKaizenOnly && saVisible && (
                      <Row style={{ marginTop: '25px' }}>
                        <S.ButtonLayoutMain onClick={openSmartAudit}>Smart Audit</S.ButtonLayoutMain>
                      </Row>
                    )}
                  </Col>
                </Auth.LoginWrapper>
              </Auth.BackgroundWrapper>
              <Auth.BottomImage>
                <Auth.FooterImage src={footer1} />
                <Auth.FooterImage src={footer2} />
                <Auth.FooterImage src={footer3} />
                <Auth.FooterImage src={footer4} />
                <Auth.FooterImage src={footer5} />
                <Auth.FooterImage src={footer6} />
                <Auth.FooterImage src={footer7} />
              </Auth.BottomImage>
            </Auth.Wrapper>
          ) : (
            <Auth.Wrapper style={{ height: 'auto', backgroundColor: 'var(--layout-header-bg-color)' }}>
              <SQDPAll useTranslation={t} language={language}></SQDPAll>
            </Auth.Wrapper>
          )}
        </>
      ) : (
        <>
          <S.LayoutMaster>
            <MainSider isCollapsed={siderCollapsed} setCollapsed={setSiderCollapsed} isRoot={checkRoot()} />
            <S.LayoutMain>
              <MainHeader isTwoColumnsLayout={isTwoColumnsLayout}>
                <Header
                  toggleSider={toggleSider}
                  isSiderOpened={!siderCollapsed}
                  isTwoColumnsLayout={isTwoColumnsLayout}
                />
              </MainHeader>
              <MainContent id="main-content" $isTwoColumnsLayout={isTwoColumnsLayout}>
                <div>
                  {homeKaizenOnly && !location.pathname.startsWith('/kaizen') ? (
                    <Navigate to="/kaizen/actions/query" replace />
                  ) : (
                    <Outlet />
                  )}
                </div>
                {!isTwoColumnsLayout && <References />}
              </MainContent>
            </S.LayoutMain>
          </S.LayoutMaster>
        </>
      )}
    </>
  );
};

export default MainLayout;
